import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { CheckCircleIcon, ClockIcon, ArrowPathIcon, ArrowLeftIcon } from '@heroicons/react/24/outline';
import Header from '../components/Header';
import { api, type ComponentDetail as ComponentDetailType } from '../services/api';
import { formatCurrency, formatHourlyWage } from '../utils/formatters';
import toast from 'react-hot-toast';

interface SubComponent {
  id: string;
  name: string;
  cost: number;
  percentage: number;
  status: 'complete' | 'analyzing' | 'queued';
}

interface ManufacturingProcess {
  id: string;
  name: string;
  description: string;
  duration: string;
  equipment: string;
  icon: string;
}

interface ProductionLocation {
  country: string;
  flag: string;
  share: number;
  description: string;
  companies?: string[];
  facilities?: Array<{
    name: string;
    address?: string;
    city?: string;
    type?: string;
    google_maps_link?: string;
  }>;
}

const ComponentDetail = () => {
  const { componentSlug } = useParams<{ componentSlug: string }>();
  const navigate = useNavigate();
  const [component, setComponent] = useState<ComponentDetailType | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load component data
  const loadComponent = async (showRefreshToast = false) => {
    if (!componentSlug) {
      setError('Component not found');
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      const componentData = await api.getComponentBySlug(componentSlug);
      setComponent(componentData);
      if (showRefreshToast) {
        toast.success('Component data refreshed');
      }
    } catch (err) {
      console.error('Failed to load component:', err);
      setError('Failed to load component data');
      if (!showRefreshToast) {
        toast.error('Failed to load component data');
      }
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  };

  useEffect(() => {
    loadComponent();
  }, [componentSlug]);

  // Auto-reload while component analysis is in progress
  useEffect(() => {
    if (!component) return;

    const isAnalyzing = component.analysis_status === 'analyzing' || component.analysis_status === 'pending';
    
    if (isAnalyzing) {
      const interval = setInterval(() => {
        loadComponent(false); // Don't show toast for auto-refresh
      }, 5000); // Refresh every 5 seconds

      return () => clearInterval(interval);
    }
  }, [component?.analysis_status]);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await loadComponent(true);
  };

  const handleAnalyzeComponent = async () => {
    if (!component || !componentSlug) return;
    
    try {
      await api.analyzeComponent(componentSlug);
      toast.success('Component analysis started! Refreshing in a moment...');
      
      // Refresh after a short delay to show the updated status
      setTimeout(() => {
        loadComponent(true);
      }, 2000);
    } catch (error) {
      console.error('Failed to start component analysis:', error);
      toast.error('Failed to start component analysis');
    }
  };

  // Calculate sub-component data from real component data
  const subComponents: SubComponent[] = component?.sub_components?.map((subComp) => ({
    id: subComp.id,
    name: subComp.name,
    cost: subComp.estimated_cost_avg || 0,
    percentage: 0, // Will be calculated based on total cost
    status: subComp.analysis_status === 'completed' ? 'complete' : 
            subComp.analysis_status === 'analyzing' ? 'analyzing' : 'queued'
  })) || [];

  // Calculate percentages for sub-components
  const totalSubComponentCost = subComponents.reduce((sum, sub) => sum + sub.cost, 0);
  if (totalSubComponentCost > 0) {
    subComponents.forEach(sub => {
      sub.percentage = Math.round((sub.cost / totalSubComponentCost) * 100 * 10) / 10;
    });
  }

  // Get manufacturing processes from component data
  const manufacturingProcesses: ManufacturingProcess[] = component?.processes?.map(process => ({
    id: process.id,
    name: process.name,
    description: process.description || 'Manufacturing process',
    duration: process.typical_duration_hours ? `${process.typical_duration_hours} hours` : 'N/A',
    equipment: process.equipment_required ? 
      (Array.isArray(process.equipment_required) ? process.equipment_required[0]?.name || 'Various equipment' : 'Various equipment') : 
      'N/A',
    icon: process.category === 'lithography' ? '🔬' :
          process.category === 'etching' ? '⚗️' :
          process.category === 'assembly' ? '🔧' :
          process.category === 'testing' ? '🧪' : '🏭'
  })) || [];

  // Get production locations from component data
  const productionLocations: ProductionLocation[] = component?.locations?.map(location => ({
    country: location.country,
    flag: location.country === 'Taiwan' ? '🇹🇼' :
          location.country === 'South Korea' ? '🇰🇷' :
          location.country === 'China' ? '🇨🇳' :
          location.country === 'Japan' ? '🇯🇵' :
          location.country === 'USA' || location.country === 'United States' ? '🇺🇸' : '🌍',
    share: location.production_percentage || 0,
    description: location.description || `${location.country} production`,
    companies: location.companies,
    facilities: location.facilities
  })) || [];

  // Loading state
  if (isLoading && !isRefreshing) {
    return (
      <>
        <title>Loading Component - ProductTrace</title>
        <div className="min-h-screen bg-gray-50">
          <Header />
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full"></div>
              <span className="ml-3 text-gray-600">Loading component details...</span>
            </div>
          </div>
        </div>
      </>
    );
  }

  // Error state
  if (error || !component) {
    return (
      <>
        <title>Component Not Found - ProductTrace</title>
        <div className="min-h-screen bg-gray-50">
          <Header />
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="text-center py-12">
              <div className="text-gray-400 text-6xl mb-4">🔧</div>
              <h1 className="text-2xl font-bold text-gray-900 mb-2">Component Not Found</h1>
              <p className="text-gray-600 mb-6">{error || 'The component you\'re looking for doesn\'t exist.'}</p>
              <div className="flex justify-center space-x-4">
                <button
                  onClick={() => navigate('/')}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Back to Home
                </button>
                <button
                  onClick={handleRefresh}
                  disabled={isRefreshing}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
                >
                  {isRefreshing ? (
                    <>
                      <div className="animate-spin w-4 h-4 mr-2 border-2 border-gray-400 border-t-transparent rounded-full" />
                      Refreshing...
                    </>
                  ) : (
                    <>
                      <ArrowPathIcon className="w-4 h-4 mr-2" />
                      Try Again
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <title>{component.name} Analysis - ProductTrace</title>
      <meta name="description" content={`Detailed analysis of ${component.name} component`} />

      <div className="min-h-screen bg-gray-50">
        <Header />
        
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Back Button and Refresh */}
          <div className="flex items-center justify-between mb-6">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center text-gray-600 hover:text-gray-900 transition-colors"
            >
              <ArrowLeftIcon className="w-4 h-4 mr-2" />
              Back
            </button>
            
            <div className="flex items-center space-x-3">
              {component && component.analysis_status === 'pending' && (
                <button
                  onClick={handleAnalyzeComponent}
                  className="inline-flex items-center px-3 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors"
                >
                  🔍 Analyze Component
                </button>
              )}
              
              <button
                onClick={handleRefresh}
                disabled={isRefreshing}
                className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                {isRefreshing ? (
                  <>
                    <div className="animate-spin w-4 h-4 mr-2 border-2 border-gray-400 border-t-transparent rounded-full" />
                    Refreshing...
                  </>
                ) : (
                  <>
                    <ArrowPathIcon className="w-4 h-4 mr-2" />
                    Refresh
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Breadcrumb */}
          <nav className="flex items-center space-x-2 text-sm text-gray-500 mb-6">
            <button onClick={() => navigate('/')} className="hover:text-gray-700">ProductTrace</button>
            <span>&gt;</span>
            <button onClick={() => navigate('/products')} className="hover:text-gray-700">Products</button>
            <span>&gt;</span>
            <button onClick={() => navigate(`/products`)} className="hover:text-gray-700">
              Products
            </button>
            <span>&gt;</span>
            <span className="text-gray-900 font-medium">{component.name}</span>
          </nav>

          {/* Component Header */}
          <div className="bg-white rounded-lg border border-gray-200 p-6 mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-4">
              🧠 {component.name} - {formatCurrency(component.estimated_cost_avg)} estimated cost
            </h1>
            
            <div className="mb-6">
              <h3 className="text-sm font-medium text-gray-700 mb-2">📋 Component Details:</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                <div>
                  <span className="text-gray-600">Category:</span>
                  <div className="font-medium text-gray-900">{component.category || 'N/A'}</div>
                </div>
                <div>
                  <span className="text-gray-600">Complexity:</span>
                  <div className="font-medium text-gray-900">{component.complexity_level || 'N/A'}</div>
                </div>
                <div>
                  <span className="text-gray-600">Sub-components:</span>
                  <div className="font-medium text-gray-900">{component.sub_components_count || 0}</div>
                </div>
              </div>
            </div>
          </div>

          {/* Impact Overview Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            {/* Environmental Impact */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <div className="flex items-center mb-4">
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center mr-3">
                  <span className="text-green-600 text-xl">🌱</span>
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Environmental Impact</h3>
              </div>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Carbon Footprint</span>
                  <span className="text-sm font-medium text-gray-900">
                    {component.environmental_impact?.carbon_footprint_kg ? `${component.environmental_impact.carbon_footprint_kg} kg CO₂` : 'N/A'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Energy Use</span>
                  <span className="text-sm font-medium text-gray-900">
                    {component.environmental_impact?.energy_consumption_kwh ? `${component.environmental_impact.energy_consumption_kwh} kWh` : 'N/A'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Water Usage</span>
                  <span className="text-sm font-medium text-gray-900">
                    {component.environmental_impact?.water_usage_liters ? `${component.environmental_impact.water_usage_liters} L` : 'N/A'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Recyclability</span>
                  <span className="text-sm font-medium text-orange-600">
                    {component.environmental_impact?.recyclability_percentage ? `${component.environmental_impact.recyclability_percentage}%` : 'N/A'}
                  </span>
                </div>
              </div>
            </div>

            {/* Labor & Manufacturing */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <div className="flex items-center mb-4">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mr-3">
                  <span className="text-blue-600 text-xl">👥</span>
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Labor & Manufacturing</h3>
              </div>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Workers Required</span>
                  <span className="text-sm font-medium text-gray-900">
                    {component.labor_requirements?.worker_count ? `${component.labor_requirements.worker_count} people` : 'N/A'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Skill Level</span>
                  <span className="text-sm font-medium text-gray-900">
                    {component.labor_requirements?.skill_level || 'N/A'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Avg. Wage</span>
                  <span className="text-sm font-medium text-gray-900">
                    {formatHourlyWage(component.labor_requirements?.average_wage_usd_hour)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Manufacturing Time</span>
                  <span className="text-sm font-medium text-gray-900">
                    {component.manufacturing_time_days ? `${component.manufacturing_time_days} days` : 'N/A'}
                  </span>
                </div>
              </div>
            </div>

            {/* Component Analysis */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <div className="flex items-center mb-4">
                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center mr-3">
                  <span className="text-purple-600 text-xl">🎯</span>
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Analysis Status</h3>
              </div>
              <div className="space-y-3 text-sm text-gray-700">
                <div className="flex justify-between">
                  <span>Analysis Status:</span>
                  <span className={`font-medium ${
                    component.analysis_status === 'completed' ? 'text-green-600' :
                    component.analysis_status === 'analyzing' ? 'text-blue-600' : 'text-gray-600'
                  }`}>
                    {component.analysis_status}
                  </span>
                </div>
                {(component.analysis_status === 'analyzing' || component.analysis_status === 'pending') && (
                  <div className="text-xs text-blue-600 flex items-center mt-2">
                    <div className="animate-spin w-3 h-3 mr-2 border border-blue-600 border-t-transparent rounded-full"></div>
                    Auto-refreshing every 5 seconds...
                  </div>
                )}
                <div className="flex justify-between">
                  <span>Sub-components:</span>
                  <span className="font-medium">{component.analyzed_sub_components}/{component.sub_components_count}</span>
                </div>
                <div className="flex justify-between">
                  <span>Complexity:</span>
                  <span className="font-medium">{component.complexity_level || 'N/A'}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Sub-Components Breakdown */}
          <div className="bg-white rounded-lg border border-gray-200 p-6 mb-8">
            <h2 className="text-xl font-bold text-gray-900 mb-6">
              📊 Sub-Component Breakdown (Click to explore):
            </h2>
            
            <div className="space-y-4">
              {subComponents.map((subComponent) => (
                <div 
                  key={subComponent.id}
                  className={`flex items-center justify-between p-4 border border-gray-200 rounded-lg transition-all ${
                    subComponent.status === 'complete' ? 'hover:shadow-md cursor-pointer hover:border-blue-300' : 'cursor-default'
                  }`}
                  onClick={() => subComponent.status === 'complete' && navigate(`/components/${subComponent.name.toLowerCase().replace(/\s+/g, '-')}`)}
                >
                  <div className="flex items-center flex-1">
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium text-gray-900">
                          {subComponent.status === 'complete' ? '🖥️ ' : ''}
                          [{subComponent.name}]
                        </span>
                        <span className="text-gray-600">{formatCurrency(subComponent.cost)} ({subComponent.percentage}%)</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div 
                          className="bg-blue-600 h-2 rounded-full"
                          style={{ width: `${subComponent.percentage}%` }}
                        ></div>
                      </div>
                    </div>
                    <div className="ml-4 flex items-center">
                      {subComponent.status === 'complete' && <CheckCircleIcon className="w-5 h-5 text-green-500" />}
                      {subComponent.status === 'analyzing' && <ClockIcon className="w-5 h-5 text-blue-500" />}
                      {subComponent.status === 'complete' && <span className="ml-2 text-sm text-green-600">✓</span>}
                      {subComponent.status === 'analyzing' && <span className="ml-2 text-sm text-blue-600">⏳</span>}
                    </div>
                  </div>
                </div>
              ))}
              
              {component.sub_components_count > subComponents.length && (
                <button className="w-full p-3 border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-50 transition-colors">
                  [▼ Show {component.sub_components_count - subComponents.length} More Sub-Components]
                </button>
              )}
            </div>
          </div>

          {/* Manufacturing Processes */}
          <div className="bg-white rounded-lg border border-gray-200 p-6 mb-8">
            <h2 className="text-xl font-bold text-gray-900 mb-6">
              🏭 Manufacturing Processes (Click to learn):
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {manufacturingProcesses.map((process) => (
                <div 
                  key={process.id}
                  className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer hover:border-blue-300"
                  onClick={() => navigate(`/processes/${process.name.toLowerCase().replace(/\s+/g, '-')}`)}
                >
                  <div className="text-center">
                    <div className="text-2xl mb-2">{process.icon}</div>
                    <h3 className="font-semibold text-gray-900 mb-2">[{process.name}]</h3>
                    <div className="space-y-1 text-sm text-gray-600">
                      <div>{process.description}</div>
                      <div>{process.duration}</div>
                      <div>{process.equipment}</div>
                    </div>
                    <div className="mt-3 text-blue-600 text-sm font-medium">
                      [→ Learn More]
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Global Production */}
          <div className="bg-white rounded-lg border border-gray-200 p-6 mb-8">
            <h2 className="text-xl font-bold text-gray-900 mb-6">
              🌍 Global Production & Labor Analysis:
            </h2>
            
            <div className="space-y-6">
              {productionLocations.length > 0 ? (
                productionLocations.map((location, index) => (
                  <div key={index} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center">
                        <span className="text-xl mr-3">{location.flag}</span>
                        <div>
                          <div className="font-medium text-gray-900">
                            [{location.country} Production] ({location.share}% share)
                          </div>
                          <div className="text-sm text-gray-600">{location.description}</div>
                        </div>
                      </div>
                      <div className="w-24 bg-gray-200 rounded-full h-2">
                        <div 
                          className="bg-blue-600 h-2 rounded-full"
                          style={{ width: `${location.share}%` }}
                        ></div>
                      </div>
                    </div>
                    
                    {/* Real data from component locations */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4 pt-4 border-t border-gray-100">
                      <div>
                        <h4 className="font-medium text-gray-900 mb-2">👥 Labor</h4>
                        <div className="space-y-1 text-sm text-gray-600">
                          <div>Workers: {component.labor_requirements?.worker_count || 'N/A'}</div>
                          <div>Wage: {formatHourlyWage(component.labor_requirements?.average_wage_usd_hour)}</div>
                          <div>Conditions: {component.labor_requirements?.working_conditions || 'N/A'}</div>
                        </div>
                      </div>
                      
                      <div>
                        <h4 className="font-medium text-gray-900 mb-2">🌱 Environment</h4>
                        <div className="space-y-1 text-sm text-gray-600">
                          <div>CO₂: {component.environmental_impact?.carbon_footprint_kg ? `${component.environmental_impact.carbon_footprint_kg}kg` : 'N/A'}</div>
                          <div>Energy: {component.environmental_impact?.renewable_energy_percentage ? `${component.environmental_impact.renewable_energy_percentage}% renewable` : 'N/A'}</div>
                          <div>Water: {component.environmental_impact?.water_usage_liters ? `${component.environmental_impact.water_usage_liters}L` : 'N/A'}</div>
                        </div>
                      </div>
                      
                      <div>
                        <h4 className="font-medium text-gray-900 mb-2">🎯 Why Here?</h4>
                        <div className="text-sm text-gray-600">
                          {location.description || 'Production location based on component analysis'}
                        </div>
                      </div>
                    </div>

                    {/* Companies and Facilities */}
                    {(location.companies || location.facilities) && (
                      <div className="mt-4 pt-4 border-t border-gray-100">
                        {location.companies && location.companies.length > 0 && (
                          <div className="mb-3">
                            <h4 className="font-medium text-gray-900 mb-2">🏢 Major Companies</h4>
                            <div className="flex flex-wrap gap-2">
                              {location.companies.map((company, idx) => (
                                <span key={idx} className="px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-sm">
                                  {company}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {location.facilities && location.facilities.length > 0 && (
                          <div>
                            <h4 className="font-medium text-gray-900 mb-2">🏭 Manufacturing Facilities</h4>
                            <div className="space-y-2">
                              {location.facilities.map((facility, idx) => (
                                <div key={idx} className="bg-gray-50 rounded-lg p-3">
                                  <div className="flex items-start justify-between">
                                    <div>
                                      <div className="font-medium text-gray-900">{facility.name}</div>
                                      {facility.city && (
                                        <div className="text-sm text-gray-600">{facility.city}</div>
                                      )}
                                      {facility.type && (
                                        <div className="text-xs text-gray-500">{facility.type}</div>
                                      )}
                                    </div>
                                    {facility.google_maps_link && (
                                      <a
                                        href={facility.google_maps_link}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="ml-3 px-3 py-1 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 transition-colors flex items-center"
                                      >
                                        📍 View on Map
                                      </a>
                                    )}
                                  </div>
                                  {facility.address && (
                                    <div className="text-sm text-gray-600 mt-1">{facility.address}</div>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <div className="text-4xl mb-2">🌍</div>
                  <div>No production location data available</div>
                  <div className="text-sm">Component analysis may still be in progress</div>
                </div>
              )}
            </div>
          </div>

          {/* Component Description */}
          <div className="bg-white rounded-lg border border-gray-200 p-6 mb-8">
            <h2 className="text-xl font-bold text-gray-900 mb-4">
              📝 Component Description:
            </h2>
            <div className="space-y-4">
              <div>
                <h3 className="font-medium text-gray-900 mb-2">Description</h3>
                <p className="text-gray-700">{component.description || 'No description available.'}</p>
              </div>
              
              {component.function && (
                <div>
                  <h3 className="font-medium text-gray-900 mb-2">Function</h3>
                  <p className="text-gray-700">{component.function}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
  };

export default ComponentDetail; 